class NotificationService {
  constructor() {
    this.notifications = this.loadNotifications();
    this.listeners = [];
  }

  // Load notifications from localStorage
  loadNotifications() {
    try {
      const saved = localStorage.getItem('crm_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading notifications:', error);
      return [];
    }
  }

  // Save notifications to localStorage
  saveNotifications() {
    try {
      localStorage.setItem('crm_notifications', JSON.stringify(this.notifications));
      this.notifyListeners();
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  }

  // Add listener for notification updates
  addListener(callback) {
    this.listeners.push(callback);
  }

  // Remove listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.notifications));
  }

  // Generate unique ID
  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Create follow-up reminder notification
  createFollowUpReminder(lead, followUpDate, priority = 'medium') {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    const followUp = new Date(followUpDate);
    followUp.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    const diffDays = Math.ceil((followUp - now) / (1000 * 60 * 60 * 24));
    
    let type = 'follow_up_due';
    let title = 'Follow-up Reminder';
    let message = '';

    if (diffDays < 0) {
      type = 'follow_up_overdue';
      title = 'Overdue Follow-up';
      message = `Follow-up with ${lead.name} is ${Math.abs(diffDays)} day(s) overdue`;
    } else if (diffDays === 0) {
      title = 'Follow-up Due Today';
      message = `Follow-up with ${lead.name} scheduled for today`;
    } else if (diffDays === 1) {
      title = 'Follow-up Due Tomorrow';
      message = `Follow-up with ${lead.name} is due tomorrow`;
    } else {
      message = `Follow-up with ${lead.name} is due in ${diffDays} days`;
    }

    const notification = {
      id: this.generateId(),
      type,
      title,
      message,
      leadId: lead._id || lead.id,
      leadName: lead.name,
      leadPhone: lead.phone,
      leadEmail: lead.email,
      followUpDate: followUpDate,
      priority,
      read: false,
      createdAt: new Date().toISOString(),
      userId: lead.assignedTo || 'admin'
    };

    this.addNotification(notification);
    return notification;
  }

  // Create lead assignment notification
  createLeadAssignmentNotification(lead, assignedEmployee) {
    const notification = {
      id: this.generateId(),
      type: 'lead_assigned',
      title: 'New Lead Assigned',
      message: `Lead ${lead.name} has been assigned to you`,
      leadId: lead._id || lead.id,
      leadName: lead.name,
      leadPhone: lead.phone,
      leadEmail: lead.email,
      priority: 'medium',
      read: false,
      createdAt: new Date().toISOString(),
      userId: assignedEmployee.id || assignedEmployee._id
    };

    this.addNotification(notification);
    return notification;
  }

  // Create follow-up completion notification
  createFollowUpCompletedNotification(lead, followUpSummary) {
    const notification = {
      id: this.generateId(),
      type: 'follow_up_completed',
      title: 'Follow-up Completed',
      message: `Follow-up completed for ${lead.name}: ${followUpSummary}`,
      leadId: lead._id || lead.id,
      leadName: lead.name,
      leadPhone: lead.phone,
      priority: 'low',
      read: false,
      createdAt: new Date().toISOString(),
      userId: lead.assignedTo || 'admin'
    };

    this.addNotification(notification);
    return notification;
  }

  // Create general reminder notification
  createReminderNotification(title, message, userId, priority = 'medium') {
    const notification = {
      id: this.generateId(),
      type: 'reminder',
      title,
      message,
      priority,
      read: false,
      createdAt: new Date().toISOString(),
      userId
    };

    this.addNotification(notification);
    return notification;
  }

  // Add notification
  addNotification(notification) {
    this.notifications.unshift(notification);
    // Keep only last 100 notifications
    if (this.notifications.length > 100) {
      this.notifications = this.notifications.slice(0, 100);
    }
    this.saveNotifications();
  }

  // Get all notifications
  getAllNotifications() {
    return this.notifications;
  }

  // Get notifications for specific user
  getNotificationsForUser(userId) {
    return this.notifications.filter(n => n.userId === userId || n.userId === 'admin');
  }

  // Get unread notifications count
  getUnreadCount(userId = null) {
    const notifications = userId ? this.getNotificationsForUser(userId) : this.notifications;
    return notifications.filter(n => !n.read).length;
  }

  // Mark notification as read
  markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.saveNotifications();
    }
  }

  // Mark all notifications as read for user
  markAllAsRead(userId = null) {
    this.notifications.forEach(notification => {
      if (!userId || notification.userId === userId || notification.userId === 'admin') {
        notification.read = true;
      }
    });
    this.saveNotifications();
  }

  // Delete notification
  deleteNotification(notificationId) {
    this.notifications = this.notifications.filter(n => n.id !== notificationId);
    this.saveNotifications();
  }

  // Delete all notifications for a specific lead
  deleteNotificationsForLead(leadId) {
    this.notifications = this.notifications.filter(n => n.leadId !== leadId);
    this.saveNotifications();
  }

  // Clean up notifications for leads that no longer exist
  cleanupStaleNotifications(currentLeads) {
    const currentLeadIds = currentLeads.map(lead => lead._id || lead.id);
    const initialCount = this.notifications.length;
    
    // Keep only notifications that either don't have a leadId or have a leadId that exists in current leads
    this.notifications = this.notifications.filter(notification => {
      if (!notification.leadId) return true; // Keep general notifications
      return currentLeadIds.includes(notification.leadId);
    });
    
    const removedCount = initialCount - this.notifications.length;
    if (removedCount > 0) {
      console.log(`Cleaned up ${removedCount} stale notifications`);
      this.saveNotifications();
    }
    
    return removedCount;
  }

  // Check for overdue follow-ups and create notifications
  checkOverdueFollowUps(leads) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check lead profile follow-ups
    leads.forEach(lead => {
      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);
            followUpDate.setHours(0, 0, 0, 0);

            // Check if already notified for this follow-up
            const existingNotification = this.notifications.find(n => 
              n.leadId === (lead._id || lead.id) && 
              n.followUpDate === followUp.nextFollowUpDate &&
              (n.type === 'follow_up_due' || n.type === 'follow_up_overdue')
            );

            if (!existingNotification) {
              if (followUpDate <= today) {
                this.createFollowUpReminder(lead, followUp.nextFollowUpDate, followUp.priority || 'medium');
              }
            }
          }
        });
      }
    });

    // Also check user-created follow-ups from Follow-Up Manager
    try {
      const userFollowUps = localStorage.getItem('crm-followups');
      if (userFollowUps) {
        const parsedFollowUps = JSON.parse(userFollowUps);
        parsedFollowUps.forEach(followUp => {
          if (followUp.scheduledDate && followUp.status === 'pending') {
            const followUpDate = new Date(followUp.scheduledDate);
            followUpDate.setHours(0, 0, 0, 0);

            // Check if already notified for this follow-up
            const existingNotification = this.notifications.find(n => 
              n.leadId === followUp.leadId && 
              n.followUpDate === followUp.scheduledDate &&
              (n.type === 'follow_up_due' || n.type === 'follow_up_overdue')
            );

            if (!existingNotification) {
              if (followUpDate <= today) {
                const lead = leads.find(l => (l.id || l._id) === followUp.leadId);
                if (lead) {
                  this.createFollowUpReminder(lead, followUp.scheduledDate, followUp.priority || 'medium');
                }
              }
            }
          }
        });
      }
    } catch (error) {
      console.error('Error checking user follow-ups for notifications:', error);
    }
  }

  // Check for upcoming follow-ups (1-3 days ahead)
  checkUpcomingFollowUps(leads) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(today.getDate() + 3);
    threeDaysFromNow.setHours(23, 59, 59, 999);

    // Check lead profile follow-ups
    leads.forEach(lead => {
      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);

            // Check if follow-up is in next 3 days
            if (followUpDate > today && followUpDate <= threeDaysFromNow) {
              // Check if already notified
              const existingNotification = this.notifications.find(n => 
                n.leadId === (lead._id || lead.id) && 
                n.followUpDate === followUp.nextFollowUpDate &&
                n.type === 'follow_up_due'
              );

              if (!existingNotification) {
                this.createFollowUpReminder(lead, followUp.nextFollowUpDate, followUp.priority || 'medium');
              }
            }
          }
        });
      }
    });

    // Also check user-created follow-ups from Follow-Up Manager
    try {
      const userFollowUps = localStorage.getItem('crm-followups');
      if (userFollowUps) {
        const parsedFollowUps = JSON.parse(userFollowUps);
        parsedFollowUps.forEach(followUp => {
          if (followUp.scheduledDate && followUp.status === 'pending') {
            const followUpDate = new Date(followUp.scheduledDate);

            // Check if follow-up is in next 3 days
            if (followUpDate > today && followUpDate <= threeDaysFromNow) {
              // Check if already notified
              const existingNotification = this.notifications.find(n => 
                n.leadId === followUp.leadId && 
                n.followUpDate === followUp.scheduledDate &&
                n.type === 'follow_up_due'
              );

              if (!existingNotification) {
                const lead = leads.find(l => (l.id || l._id) === followUp.leadId);
                if (lead) {
                  this.createFollowUpReminder(lead, followUp.scheduledDate, followUp.priority || 'medium');
                }
              }
            }
          }
        });
      }
    } catch (error) {
      console.error('Error checking upcoming user follow-ups for notifications:', error);
    }
  }

  // Get today's follow-ups (including user-created follow-ups)
  getTodaysFollowUps(leads, userId = null) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todaysFollowUps = [];

    // Check lead profile follow-ups
    leads.forEach(lead => {
      // Filter by user if specified
      if (userId && lead.assignedTo !== userId) return;

      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);
            followUpDate.setHours(0, 0, 0, 0);

            if (followUpDate.getTime() === today.getTime()) {
              todaysFollowUps.push({
                ...followUp,
                lead: lead,
                leadName: lead.name,
                leadPhone: lead.phone,
                leadEmail: lead.email
              });
            }
          }
        });
      }
    });

    // Also check user-created follow-ups from Follow-Up Manager
    try {
      const userFollowUps = localStorage.getItem('crm-followups');
      if (userFollowUps) {
        const parsedFollowUps = JSON.parse(userFollowUps);
        parsedFollowUps.forEach(followUp => {
          if (followUp.scheduledDate && followUp.status === 'pending') {
            const followUpDate = new Date(followUp.scheduledDate);
            followUpDate.setHours(0, 0, 0, 0);

            if (followUpDate.getTime() === today.getTime()) {
              todaysFollowUps.push({
                ...followUp,
                lead: leads.find(l => (l.id || l._id) === followUp.leadId),
                leadName: followUp.leadName,
                leadPhone: followUp.leadPhone,
                leadEmail: followUp.leadEmail,
                nextFollowUpDate: followUp.scheduledDate
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading user follow-ups for today:', error);
    }

    return todaysFollowUps;
  }

  // Get overdue follow-ups
  getOverdueFollowUps(leads, userId = null) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const overdueFollowUps = [];

    leads.forEach(lead => {
      // Filter by user if specified
      if (userId && lead.assignedTo !== userId) return;

      if (lead.followUps && lead.followUps.length > 0) {
        lead.followUps.forEach(followUp => {
          if (followUp.nextFollowUpDate && !followUp.completed) {
            const followUpDate = new Date(followUp.nextFollowUpDate);
            followUpDate.setHours(0, 0, 0, 0);

            if (followUpDate < today) {
              const daysOverdue = Math.ceil((today - followUpDate) / (1000 * 60 * 60 * 24));
              overdueFollowUps.push({
                ...followUp,
                lead: lead,
                leadName: lead.name,
                leadPhone: lead.phone,
                leadEmail: lead.email,
                daysOverdue
              });
            }
          }
        });
      }
    });

    return overdueFollowUps.sort((a, b) => b.daysOverdue - a.daysOverdue);
  }

  // Clear all notifications
  clearAllNotifications() {
    this.notifications = [];
    this.saveNotifications();
  }

  // Force cleanup of all stale notifications immediately
  forceCleanupStaleNotifications() {
    // Remove all notifications that have leadId but no corresponding lead data
    const initialCount = this.notifications.length;
    this.notifications = this.notifications.filter(notification => {
      // Keep notifications without leadId (general notifications)
      return !notification.leadId;
    });
    
    const removedCount = initialCount - this.notifications.length;
    if (removedCount > 0) {
      console.log(`Force cleaned up ${removedCount} stale notifications`);
      this.saveNotifications();
    }
    
    return removedCount;
  }

  // Helper to safely get the browser's Notification object
  getSafeNotification() {
    try {
      if (typeof window !== 'undefined' && 'Notification' in window && window.Notification) {
        return window.Notification;
      }
    } catch (e) {}
    try {
      if (typeof Notification !== 'undefined') {
        return Notification;
      }
    } catch (e) {}
    return null;
  }

  // Browser notification (if permission granted)
  showBrowserNotification(title, message, icon = null) {
    try {
      const SafeNotification = this.getSafeNotification();
      if (SafeNotification && SafeNotification.permission === 'granted') {
        new SafeNotification(title, {
          body: message,
          icon: icon || '/favicon.ico',
          badge: '/favicon.ico'
        });
      }
    } catch (error) {
      console.warn('Failed to show browser notification:', error);
    }
  }

  // Request browser notification permission
  requestNotificationPermission() {
    try {
      const SafeNotification = this.getSafeNotification();
      if (SafeNotification) {
        if (SafeNotification.permission === 'default') {
          const req = SafeNotification.requestPermission();
          if (req && typeof req.then === 'function') {
            return req.catch(() => 'denied');
          }
        }
        return Promise.resolve(SafeNotification.permission);
      }
    } catch (error) {
      console.warn('Failed to request notification permission:', error);
    }
    return Promise.resolve('denied');
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
